// Learner-facing CCS copy — single source of truth (KAN-244, Chen's first-week
// reframe: "explore your future," not a class). Change WORDING HERE, not inline in
// the pages, so a copy pass is one file, not a scavenger hunt.
//
// Display copy ONLY. Nothing here is referenced by the API, telemetry event names,
// objective IDs, or the `bout` internals. Learner-facing "Future Challenge" is the
// backend `bout`; learner-facing "Starting Point" is the FC assessment. Code
// identity is unchanged on both sides.
//
// This file MIRRORS the two-register table in outcomes §2.0, which is canonical.
// If the two disagree, §2.0 wins and this file is wrong.
//
// Increment 3 (Paul). Two rules this file is written to, both load-bearing:
//   1. Copy describes what the system OBSERVED, never what the learner IS. No
//      superlatives about the person. "Strongest signal," not "where you're strongest."
//   2. Copy never claims something the system hasn't verified yet. Before a
//      re-check we do not know that anything was learned, so we don't say so.
//   3. ONE VOICE across every surface. Rules 1 and 2 now appear on five surfaces
//      (reveal states, return promise, growth reveal, open-attempt gate, re-check
//      result). If they drift in tone the discipline reads as inconsistency rather
//      than principle.
//      The vocabulary is deliberately small:
//        - a challenge WORKED ON a capability. Never "improved", "built",
//          "developed", "boosted". Those are gain claims.
//        - "STUCK" is the open question, used before verification.
//          "HELD" is the answer, used only after a passed cold proof.
//          They are not synonyms and should not be unified.
//        - "Here's what ..." opens a reveal. Same construction each time.
//        - an em-dash carries the honest qualifier: "From a challenge — not
//          confirmed yet."
//      New copy joins this vocabulary rather than introducing its own.

export const LEARN_COPY = {
  // KAN-281 v0.2 — the evidence-tier words moved to the canonical fixture
  // (config/evidenceTierTreatment.fixture.json) and are read through config/evidenceTier.js,
  // so a tier is spelled ONE way everywhere and Paul's Flare build asserts against the same
  // file. The old LEARN_COPY.evidenceTier block was removed to avoid a second, drifting home.

  // The vision-first screen shown once before setup.
  vision: {
    title: 'A new way to learn for the AI era',
    body:
      'Novara puts you in real situations, shows you what your choices reveal, and ' +
      'gives you something specific to work on next. This isn’t a course. It’s a ' +
      'short challenge each day that adapts to you.',
    // Chen's loop. Test-asserted — see CcsOnboardingPage.test.js.
    loop: ['Challenge', 'Explore', 'Learn', 'Improve'],
    loopCaption:
      'Take on a short challenge → see what it reveals → work on the gap → come ' +
      'back later and find out what stuck.',
    cta: 'Start exploring',
  },

  onboarding: {
    title: 'Let’s get you set up',
    intro:
      'A few quick questions so we can aim your first challenges. Nothing on these ' +
      'screens is scored, and you can change your answers later.',
    steps: ['You', 'Your time', 'How we start'],
    next: 'Next',
    back: 'Back',
    age: {
      q: 'What year were you born?',
      blurb:
        'This helps us set things up correctly. If you’re under 18, we’ll ask a parent ' +
        'or guardian to approve before you start.',
      placeholder: 'e.g. 1998',
      guardian:
        'Because you’re under 18, a parent or guardian will be asked to approve before ' +
        'your first challenge — you can still finish setting up now.',
    },
    time: {
      q: 'How much time do you want to put in?',
      blurb:
        'This sets how much we schedule each day. It changes the pace, never how far ' +
        'you can get. You can adjust it later.',
      minutesLabel: 'Minutes a day',
      daysLabel: 'Days a week',
    },
    path: {
      q: 'How would you like to start?',
      // Not "either way we end up in the same place" — a probed conversation and a
      // four-item self-rating are not equivalent evidence, and the system treats
      // them differently. Don't tell the learner otherwise.
      blurb: 'Both work. Pick whichever feels easier right now.',
      interviewTitle: 'Talk it through',
      interviewBlurb: 'A short back-and-forth about your experience.',
      selfTitle: 'Quick self-rating',
      selfBlurb: 'Rate four areas in under a minute.',
    },
    startCta: 'I’m ready',
    errors: {
      save: 'Could not save your details. Please try again.',
      answers: 'Could not save your answers. Please try again.',
    },
  },

  // The FC assessment. Learner-facing name is "Starting Point" — it had to avoid
  // "future" (collides with Future Challenge two screens later), avoid implying a
  // test or a score, and sit next to "Here's what we heard" without contradicting
  // it. It also describes what the thing actually produces.
  assessment: {
    name: 'Starting Point',
    intro:
      'A short set of situations to find your starting point. There’s no pass mark ' +
      'and no time limit — just pick what you’d actually do.',
    progress: 'Situation {n} of {total}',
    resume: 'Picking up where you left off — same situations, same order.',
    complete: 'That’s your starting point. Here’s what it showed.',
    // Shown when the learner heads into their first challenge with an unfinished
    // Starting Point. This is an explicit, plainly-worded choice by design — the
    // sealing rule in the attempt-state contract is only defensible because the
    // learner was told what they were giving up. Never a toast, never dismissible,
    // never a silent transition. See novara_onboarding_session_state_v0_2 §4.3.
    openAttemptGate: {
      title: 'You’ve got {remaining} situations left',
      body:
        'You can go on now if you’d rather. It’s about {minutes} minutes to finish, ' +
        'and the Starting Point only happens once — anything you skip, we won’t have ' +
        'a read on.',
      finishCta: 'Finish those first',
      skipCta: 'Go on to The Boundary',
    },
  },

  // Moved in from CcsConstellationPage.js so the whole first-week voice is in one
  // file. These are the strings most likely to drift into a capability claim.
  reveal: {
    header: 'Here’s what we heard',
    // Labels the SIGNAL, not the person. Do not change to "where you're strongest"
    // or any variant that describes the learner rather than the evidence.
    strongestSignalLabel: 'Strongest signal',
    strongestSignalLegend: 'This came through most clearly in what you told us.',
    evidencePrompt: 'Tap any point to see what you said.',
    // Shown when stars exist but nothing has been earned in a challenge yet.
    freshRevealNote:
      'This is your starting point, built from your own answers. Nothing here is ' +
      'measured yet — that comes from the challenges.',
    // The returning (non-fresh) constellation view — Paul's final strings (KAN-145).
    // returningHeader can't reuse "Here's what we heard": by the return visit the map
    // mixes bout placements with interview priors, so "what we heard" would describe a
    // mixed set as if it were all self-report. "Where things stand" is accurate across
    // every combination and makes no claim about the person. stateLegend tells the
    // learner HOW to read the map (provenance, not ranking) so seeded and reported
    // don't read as a ladder — the four `states` below carry the per-point provenance.
    stateLegend: 'Each point shows where it came from.',
    returningHeader: 'Where things stand',
    // DRAFT, pending 151.D / KAN-243. Four learner-visible states. The distinction
    // between seeded and reported is PROVENANCE, not confidence — where it came
    // from, not how sure we are. Copy must not rank them against each other.
    states: {
      reported: 'From what you told us.',
      seeded: 'From your Starting Point.',
      provisional: 'From a challenge — not confirmed yet.',
      measured: 'Confirmed after a gap. This one held.',
    },
    // Screen-level thin state (KAN-151, Paul): shown when the constellation is sparse
    // (a short conversation / self-rating only). Attributes the sparseness to how much
    // we've collected, not to the learner; avoids negation on purpose ("not a verdict"
    // would plant the reading it denies).
    thinConstellationNote:
      'Only a few points so far — that’s what a short conversation gives us. ' +
      'Challenges are what fill it in.',
    // At/below this many stars a fresh reveal is "thin" and shows the note above.
    // 5 catches the short-conversation case (self-rating floors at 4, a short interview
    // ~3-4) and gets out of the way once the map fills in. NB: this controls DENSITY,
    // not evidence strength — at 5 stars a learner can still be entirely `reported`
    // (no task performed). What stops a self-report picture reading as a finding is
    // `freshRevealNote` + the four `states` labels, which don't move with the count.
    thinConstellationThreshold: 5,
  },

  challenge: {
    // Resolution on the "Future Challenge" / "Future Capability" collision: the
    // bout keeps Chen's word and the assessment took a non-colliding learner name
    // ("Starting Point") instead. Test-asserted — CcsBoutPage.test.js is unchanged.
    badgeFallback: 'Future Challenge',
    // L-6 item 3: Day 6 shows the learner's own Days 1-5. A day not done is said plainly and
    // left empty: nothing is filled in for it, and it is not a mark against anyone.
    priorHeading: 'Your earlier days',
    priorDay: (day) => `Day ${day}`,
    priorPuzzle: 'The opening puzzle (no written answer).',
    priorNotDone: 'Not done.',
    // KAN-555 follow-up: labels for a day's later answers. KAN-506: Paul's screen wording v0.2,
    // section 7 (earlierSecondTryAfterAi, earlierRevised, earlierApplied); `other` is a PLACEHOLDER.
    priorLater: {
      after_ai: 'Second try, after the AI chat',
      revision: 'New answer, after help',
      application: 'Answer on a new case',
      other: 'Later answer:',                       // PLACEHOLDER
    },
    startError: 'Couldn’t start your challenge. Please try again.',
    nextChallenge: 'Next challenge',
    moveOn: 'Move on',
    // KAN-560 (Paul, Day 1 run): a journey day's last button and its header badge.
    backToDashboard: 'Back to dashboard',
    dayBadge: (n) => `Day ${n}`,
    // Spark (D1) is an instant-learning dose, not a test: the feedback carries
    // the insight the item was built to deliver, and no verdict. A Spark screen
    // therefore shows no score, no right/wrong, and no retry — being told
    // "3 / 3, that holds up" teaches nothing the insight doesn't, and being told
    // "not quite" turns one sharp rep into a grade. Focus and Stretch keep the
    // scored treatment: by then the learner is working, not being shown a thing.
    sparkInsightHeading: 'Here’s what matters',
    sparkContinue: 'Continue',

    // KAN-487 — the RECORDED state for a typed response. Nothing is graded
    // synchronously, so this block must not carry a verdict, a credit or a retry.
    // Moved here from hardcoded JSX in CcsBoutPage (2026-09-18): its sibling
    // verdict block already read from this module, and copy that lives in one
    // place is copy that can be changed in one place. Paul asked on KAN-487
    // whether it had a zh key — it had no key at all, which is the answer.
    //
    // Rater status is deliberately NOT surfaced: that is our state to track, not
    // the learner's.
    crRecordedHeading: 'Your response is recorded.',
    crRecordedBody:
      'It is being read against what this challenge was looking for. '
      + 'Nothing about it is decided yet.',
    // Learning loop step 3 (28 Sep 2026): the feedback that replaces "recorded" once the answer
    // has been read. It names what the answer did not yet do and one thing to try next; it never
    // carries a score or a level.
    crMissingHeading: 'What your answer doesn\u2019t do yet',
    crCompleteHeading: 'Your answer covers what this challenge was looking for.',
    crNextStepLabel: 'Try next:',
    crWithReviewerBody:
      'A person on our team will read this one. Nothing about it is decided yet.',
    crTimedOutBody:
      'It is still being read. Nothing about it is decided yet.',
    // Paul's pass rule (KAN-439): an answer that doesn't take on the task is insufficient
    // evidence, never a low score. Said plainly, with what the task asks and what to try.
    crInsufficientHeading: 'This answer doesn\u2019t take on the question yet.',
    crInsufficientBody: 'Nothing about your level was recorded from it.',
    // KAN-517: the V9 adaptive follow-up (one question or a hint at a time; at most two rounds).
    fuQuestionHeading: 'A follow-up question',
    fuHelpHeading: 'A hint',
    fuThinking: 'Reading your answer…',
    fuReplyLabel: 'Your reply',
    fuReplySubmit: 'Send reply',
    fuStop: 'Stop here',
    fuError: 'Something went wrong. You can try again or stop here.',
    // KAN-506 (Paul, Jira 14244): an optional note on AI use, under a typed answer and under a
    // follow-up reply. Same wording for every age group. Sent as ai_use_note; never scored.
    aiNoteLabel: 'If AI helped with this answer, what did it do? (optional)',
    aiNoteHint: 'Say what the AI gave you, and what you checked, chose or changed yourself. This isn\u2019t scored.',
    aiNotePlaceholder:
      'e.g. \u201cIt suggested two ideas. I used the second and changed the example to fit my situation.\u201d',
  },

  // The honesty rail, and why this panel is the hardest place to hold it: it
  // renders immediately after a challenge, when the learner most wants to be
  // told they got better, and before anything has been verified. So it says what
  // the challenge WORKED ON and how tomorrow checks whether it stuck. It never
  // says "you improved X" — Aaron's backend test fails the build if the payload
  // carries `improved` / `gain` / `measured_rank`, so the rail is enforced in
  // code and not only here.
  //
  // Chen's "feel yourself becoming stronger" and the rule "don't claim what a
  // measurement hasn't earned" are the same product: direction plus
  // verification-in-progress is a loop the learner is inside, and that is more
  // motivating than an unearned claim, not less.
  growthReveal: {
    // Same construction as reveal.header, deliberately — see voice rule 3.
    header: 'Here’s what that worked on',

    workedOn: {
      label: 'What you worked on',
      // {capability} is the dimension the challenge exercised. "Worked on",
      // never "improved" — nothing has been verified at this point.
      body: 'That one worked on {capability}.',
    },

    whyItMatters: {
      label: 'Why it matters',
      // Sourced from the Opportunity bridge. Only approved lines are shown; with
      // none, the panel leaves this section out entirely (KAN-518, Paul).
    },

    next: {
      label: 'What’s next',
      // A prescription violation yields no next rather than an error, so a
      // learner will genuinely meet this state.
      none: 'Nothing queued right now — check back tomorrow.',
    },

    // The load-bearing block. Two states, deliberately different sentences: this
    // panel renders after EVERY challenge, so one fixed line becomes wallpaper
    // inside a week and the rail stops registering.
    //
    // There is NO `held` here, by design. The growth reveal fires immediately
    // after a challenge and never witnesses a passed cold proof — only the
    // Proving Grounds re-check does. `verify.state` is computed server-side and
    // the panel renders `growthReveal.verify[state]`, so held cannot be reached
    // by construction rather than by anyone remembering the rule. Same principle
    // as the build-failing test on `improved`. See `recheckResult` below.
    verify: {
      label: 'How we’ll know it stuck',
      // Before the gap has elapsed. Names the mechanism and why it matters.
      pending:
        'We’ll come back to this on fresh material after a gap. Right now it’s ' +
        'still warm in your memory — the gap is what tells the difference.',
      // Gap elapsed, re-check available.
      due:
        'This one’s ready to check. Fresh material, nothing you’ve seen before — ' +
        'that’s the part that counts.',
    },
  },

  // The Proving Grounds re-check result — the surface that owns `held` (KAN-250,
  // Paul). This is the ONE screen in the product where a claim has been earned: a
  // cold proof, after a gap, on material the learner hasn't seen is exactly what
  // the claim requires, so here we say it plainly. Everywhere else in this file
  // withholds; withholding here would be its own kind of dishonesty.
  //
  // The hard state is `notHeld`. It is not a failure of the learner and it is not
  // a consolation prize — it is the instrument doing the job it exists for. The
  // subject stays the capability, never the person: "this one didn't hold", never
  // "you didn't retain it".
  //
  // A re-check bout is single-subfacet (start_recheck(subfacet_key) → one
  // construct), so a sitting resolves exactly one construct: `held` or `notHeld`.
  // `mixed` is retained for a future batched multi-construct re-check and is
  // dormant under today's model. `insufficient` covers a proof set too thin to read.
  recheckResult: {
    header: 'Here’s what held',
    headerNone: 'Here’s how that landed',

    // Earned. The only assertion of its kind in the product.
    held:
      'This one held. You did it again days later, on material you hadn’t seen.',

    // Honest, forward-pointing, not softened. "Yet" carries the direction
    // without promising an outcome.
    notHeld:
      'This one didn’t hold yet. You had it right after the challenge; days ' +
      'later, on something you hadn’t seen, it didn’t come back. That gap is ' +
      'the whole reason we check — and it’s the thing practice closes.',

    // A split sitting. Named separately so neither result is averaged away.
    // Dormant until a batched multi-construct re-check exists (see note above).
    mixed:
      '{heldCount} of these held. {notHeldCount} didn’t — that’s where the next ' +
      'few challenges go.',

    // Below the evidence floor, or the proof set was too thin to read. Not a
    // result; an instrument that hasn't reported.
    insufficient:
      'Not enough here to call it yet. A few more challenges and this one ' +
      'becomes checkable.',

    // What the screen means, in one line under the result.
    legend:
      'Held means you did it cold, on a surface you hadn’t seen, after a gap. ' +
      'That’s the only thing we count as measured.',

    nextCta: 'Keep going',
  },

  recheck: {
    // The return promise. Two changes from the draft:
    //   - not "what you learned" — whether anything was learned is exactly what
    //     the re-check is for. Claiming it beforehand gives away the finding.
    //   - not "tomorrow" — the consolidation gap is a configured value, not a day.
    //     If the gap is 3 days, copy that says tomorrow is a promise we break.
    //     `days` should be passed from the scheduler, not hardcoded here.
    returnPromise: 'Come back in a few days and put it to work — that’s how we find out what stuck.',
    returnPromiseWithDays: (days) =>
      `Come back in ${days === 1 ? 'a day' : `${days} days`} and put it to work — ` +
      'that’s how we find out what stuck.',
  },

  // KAN-249 — Future Opportunity bridge (the "where this leads" surface + the
  // six-stage journey). DRAFT framing copy: these strings are NOT yet in outcomes
  // §2.0 and are FLAGGED TO PAUL for canonization (register mechanism, KAN-248) —
  // treat as provisional until he lands final wording. The per-capability content
  // (opportunity themes / why_it_matters) is Ray/Paul's curated map and is withheld
  // until an entry is content_status="approved"; until then the surface shows the
  // journey (direction) + the honest `pending` line, never a stub. Honesty rail
  // (KAN-205): direction and possibility only — never a claim the learner HAS an
  // opportunity or WILL get an outcome.
  opportunityBridge: {
    header: 'Where this leads',
    intro:
      'The capabilities you build here point somewhere. This is the direction — ' +
      'not a promise, and not where you are yet.',
    // Per-status micro-labels for the six stages (labels themselves come from the
    // server — Chen's terms).
    stageStatus: {
      live: 'Open now',
      emerging: 'Starting here',
      coming: 'Coming',
    },
    // Shown for the "where this leads" line while the curated map is still
    // placeholder (nothing approved yet) — an honest gap, not filler.
    pending:
      'We’re still mapping where your capabilities lead. It’s coming — and it won’t ' +
      'claim more than the work shows.',
  },

  // KAN-555 (part 2) — the learning set's optional steps: help and revise or apply to a new case,
  // core task first or postpone, and the learner's own question (opportunity challenge 10).
  // KAN-506: Paul's screen wording v0.2 (kan506_screen_wording_v0_2.md, sections 2-4 and 7),
  // verbatim. The learner doesn't choose revise or apply; the system shows one of the two. The new
  // case and its ask (section 3a, served by the server as new_case.text and new_case.ask), the
  // revise prompt, the core task titles and the scope of the learner's own question come from the
  // server; "One idea that might help" is written by the system (never the item's support_text as
  // written). Keys marked PLACEHOLDER are not in Paul's file (copy needed).
  learningSteps: {
    helpHeading: 'One idea that might help',
    reviseHeading: 'Try it again',
    revisePrompt: 'Use what you just learned to improve your answer. Your first answer stays saved.',
    reviseSubmit: 'Save my new answer',
    applyHeading: 'Try it on a new case',
    // v0.2: a plain heading line; the case (applyCase = new_case.text) and the ask
    // (applyAsk = new_case.ask) follow, each as its own paragraph, as the server sends them.
    applyIntro: "Here's a change to the situation:",
    applyNote: 'Your first answer stays saved.',
    applySubmit: 'Save my answer',
    // Learning 17 and 29 (the system plays a user). Revise only until the simulated user is built,
    // so nothing shows this yet.
    simulatedLabel: 'This feedback is from a pretend user, not a real person.',
    pauseOption: 'Come back to this later',
    pauseNote: 'You can pick this up any time. Nothing is marked against you.',
    roaAnswerLabel: 'Your answer',                 // PLACEHOLDER
    roaSaved: 'Saved.',                            // PLACEHOLDER
    // Section 4: integrated challenges, earlier work missing.
    cfpHeading: 'This challenge builds on earlier work',
    cfpBody: "It uses challenges you haven't done yet. You can do one of those first, or come back to this one later.",
    cfpCoreFirst: 'Do an earlier challenge first',
    cfpPostpone: 'Come back to this later',
    cfpNote: 'Either is fine. Nothing is marked against you.',
    // v0.2 coreTaskListHeading: one heading over the list of core task titles.
    cfpCoreListHeading: 'Earlier challenges you can do first',
    cfpRecorded: 'Recorded as postponed.',         // PLACEHOLDER
    cfpOfferedAgain: 'This task was postponed earlier.',   // PLACEHOLDER
    // KAN-555 follow-up: the button that opens a listed core task (v0.2 coreTaskOpen).
    cfpOpenCore: 'Open',
    // Section 2, opportunity #10 (own question). The prompt under it is the item's own text.
    oqLabel: "Haven't done the earlier challenges yet? You can choose your own question instead.",
    error: 'Something went wrong. Please try again.',   // PLACEHOLDER
    // Section 7, earlier work: how each saved answer is labeled when the learner looks back. The
    // three later-answer labels are shown today through LEARN_COPY.challenge.priorLater; these
    // three have no screen yet (copy added, no screen yet).
    earlierFirstTry: 'First try',
    earlierUpdatedAfterNewInfo: 'Updated answer, after new information',
    earlierOwnQuestion: 'Your own question',
  },

  profile: {
    standingEmpty:
      'Your standing appears once you’ve taken on a few challenges.',
    // KAN-125 copy standard (Paul v0.2). Wording owned by Paul — change here, not in the component.
    provisionalTag: 'provisional',       // Tier B: level shown but the estimate is imprecise (high SE)
    levelUnclear: 'no clear read',        // sub-chance / non-engaged response pattern — no level word
    rankingComing:                        // §5/D2: generalized forward-looking line, NOT "city ranking"
      'Ranking will appear once enough members complete the assessment.',
  },

  // KAN-555 (part 1): item display modes for the 84-challenge format. KAN-506: Paul's screen
  // wording v0.2 (kan506_screen_wording_v0_2.md, sections 1, 2 and 5), verbatim. Keys marked
  // PLACEHOLDER are not in Paul's file (copy needed). Shown only on items imported with
  // display_mode criteria_up_front or task_then_ai, and on held-back new information.
  displayModes: {
    // Section 1: sample week, criteria shown up front.
    taskHeading: 'Your challenge',
    requirementsHeading: 'What to include',
    criteriaHeading: "What we'll look at",
    criteriaNote: "Short, clear answers count as much as long ones. If you're not sure about something, you can say so.",
    submit: 'Send my answer',
    // Follow-ups (one at a time, at most two). Not wired here: the follow-up exchange for
    // recorded-only items is a separate change.
    followupHeading: 'One more question',
    followupNote: "This is about your answer. You don't need to repeat what you've already said.",
    followupSubmit: 'Send',
    // Day 3 new information (also learning 03 and 15), shown only after the first answer is saved.
    newInfoHeading: 'Something new',
    newInfoIntro: "Your first answer is saved. Here's new information:",
    newInfoPrompt: 'Does this change your thinking? Keeping your choice or changing it are both fine. Say why.',
    newInfoSubmit: 'Send my updated answer',
    // Section 2: opportunity challenges, first try, then AI, then second try.
    stepLabels: ['Your first try', 'Talk it through with AI', 'Your second try'],
    outputHeading: "What you'll make",
    observationHeading: "What we'll look at",
    firstTrySubmit: 'Save my first try',
    firstTryLockNote: "Your first try is saved as it is. It shows where you started, so it can't be changed.",
    chatHeading: 'Talk it through with AI',      // = stepLabels[1]
    chatIntro: 'Now you can ask AI for ideas, questions or a different view. You decide what to use.',
    chatLimit: (n) => `You have ${n} messages.`,
    // The remaining count: "1 message left" when exactly one remains (v0.2 chatRemainingOne).
    chatRemaining: (n) => (n === 1 ? '1 message left' : `${n} messages left`),
    chatEnd: "That's the end of the chat. Use whatever helped in your second try.",
    chatSkip: 'Skip the chat',
    secondTryHeading: 'Your second try',
    secondTryNote: 'Change what you want and keep what still works. Say what you changed and why.',
    secondTrySubmit: 'Save my second try',
    // #29 (the system plays a user). Not built yet, so nothing shows this yet.
    simulatedLabel: 'This feedback is from a pretend user, not a real person.',
    chatWaiting: 'Available after your first answer is saved.',   // PLACEHOLDER
    // Section 5: the chat input. The placeholder also names the box for screen readers.
    chatInputPlaceholder: 'Ask a question or share an idea',
    chatSend: 'Send',
    chatError: 'Could not reach the AI. Please try again.',       // PLACEHOLDER
    roleLearner: 'You',                          // PLACEHOLDER
    roleAi: 'AI',                                // PLACEHOLDER
    secondWaitsForChat: 'Open the AI chat to continue.',          // PLACEHOLDER
  },

  // KAN-488 (no session above the learner's level): the plan read's next-step line when the plan
  // found no session at the learner's level and so serves nothing from its focus. NEUTRAL
  // PLACEHOLDER — copy needed from Paul. Shown only when the server sets
  // read.no_session_at_level (the server leaves next_step empty then).
  planRead: {
    noSessionAtLevel: 'Sessions for this are not ready yet.',
  },

  // KAN-555 (part 3): the multi-week journey for the 84-challenge set, on the /learn dashboard.
  // Neutral placeholders (copy needed from Paul) EXCEPT weekLabel, dayLabel, introduction (Day 7),
  // dayState.done and the day* keys, which are Paul's screen wording v0.2, section 7. Shown only to a learner
  // the server reports on the c84 journey (`journey: 'c84'`), which nobody is until
  // settings.entry_journey is switched to "c84". Numbers come from the payload, never the words.
  entryJourney: {
    weekDay: (week, day) => `Week ${week}, Day ${day}`,
    weekOf: (days) => `${days} days this week`,
    ready: (week, day) => `Week ${week}, Day ${day} is available.`,
    waiting: (week, day, days) =>
      `Week ${week}, Day ${day} has been available for ${days === 1 ? '1 day' : `${days} days`}.`,
    opens: (week, day, date) => `Week ${week}, Day ${day} opens ${date}.`,
    complete: 'All weeks done.',
    // Paul's screen wording v0.2, section 7 (Day 7): the Day 7 entry in the week strip.
    introduction: 'Day 7: About you',
    introductionDue: 'Day 7: introduction is due.',
    start: (week, day) => `Start week ${week}, day ${day}`,
    continue: (week, day) => `Continue week ${week}, day ${day}`,
    weekLabel: (week) => `Week ${week}`,
    dayLabel: (day) => `Day ${day}`,
    // Day states. v0.2 section 7: done = dayDone; an available day with no answer yet =
    // dayNotStarted; an upcoming day with a known date = dayOpensTomorrow / dayOpensOn. The rest
    // are PLACEHOLDERS (an available day already started, an upcoming day with no date, due).
    dayState: {
      done: 'Done', available: 'available', upcoming: 'not yet open', due: 'due',
    },
    dayNotStarted: 'Not started',
    dayOpensTomorrow: 'Opens tomorrow',
    dayOpensOn: (weekday) => `Opens ${weekday}`,
    // Copy added, no screen yet: nothing marks a journey day as saved for later today.
    daySavedForLater: 'Saved for later',
    // Day 7 (v0.2 section 7). day7Label is `introduction` above. Copy added, no screen yet: the
    // dashboard has no Day 7 card with a body and a Start button (its button is introductionOpen).
    day7Body: 'Tell us about your goals and how your first week went. It helps us suggest what to work on next.',
    day7Start: 'Start',
    // Day 7 button on the dashboard (V9 week and c84 journey alike).
    introductionOpen: 'Open Day 7',
  },

  // Day 7 — the Detailed Profile form page (/learn/detailed-profile).
  // COPY NEEDED FROM PAUL: every string in this block is a NEUTRAL PLACEHOLDER, not approved
  // wording. The QUESTIONS are not here: they come from the question file, verbatim from the
  // source (Mr. Chen's Day 7 personal introduction and questionnaire).
  detailedProfile: {
    title: 'Day 7',
    optional: '(optional)',
    keepLabel: 'Saved earlier:',
    saveDraft: 'Save and finish later',
    draftSaved: 'Saved.',
    submit: 'Submit',
    submitted: 'Submitted.',
    alreadySubmitted: 'Already submitted.',
    notDue: 'Not open yet.',
    notReady: 'Not available yet.',
    noQuestions: 'Not available for your account.',
    notPermitted: 'Not available until participation is approved.',
    invalid: 'Some answers need a change.',
    problem: 'Please check this answer.',
    pickUpTo: (n) => `Up to ${n}`,
    loadError: 'Could not load. Please try again.',
    saveError: 'Could not save. Please try again.',
    retry: 'Try again',
    back: 'Back to dashboard',
  },
};

export default LEARN_COPY;
